import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import CelebrationIcon from "images/designB/celebrationicon.png";
import { useData } from "data/data-provider";
import moment from "moment";
import { formatPrice } from "utils/convertNumbThousand";

interface PaymentSuccessProps {
  amount?: string;
  bookingId?: string;
  date?: string;
  time?: string;
  paymentMethod?: string;
  lastFourDigits?: string;
  email?: string;
  onComplete?: () => void;
}

export default function PaymentSuccess({
  amount,
  bookingId,
  date,
  time,
  paymentMethod = "Visa",
  lastFourDigits,
  email,
  onComplete,
}: PaymentSuccessProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [progress, setProgress] = useState(100);

  const { bookingdetails, ourCompany } = useData();

  useEffect(() => {
    const startTime = Date.now();
    const duration = 5000; // 5 seconds

    // Update countdown and progress based on elapsed time
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const remainingSeconds = Math.ceil(remaining / 1000);
      const progressPercent = (remaining / duration) * 100;

      setCountdown(remainingSeconds);
      setProgress(progressPercent);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 100); // Update every 100ms for smooth progress

    // Auto-redirect after exactly 5 seconds
    const timer = setTimeout(() => {
      navigate(`/booking-details?id=${bookingdetails?.unique_identifier}`);
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [bookingdetails, navigate]);

  const handleCopyBookingId = () => {
    navigator.clipboard.writeText(bookingdetails?.unique_identifier || "");
    toast.success("Booking ID copied to clipboard");
  };

  const handleComplete = () => {
    navigate(`/booking-details?id=${bookingdetails?.unique_identifier}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8 md:p-12">
        {/* Celebration Icon */}
        <div className="flex justify-center mb-6">
          <img src={CelebrationIcon} alt="Celebration" />
        </div>

        <div className="max-w-xs mx-auto">
          <h1 className="text-3xl font-bold text-center text-[#18181B] mb-2">
            Payment Successful
          </h1>

          <p className="text-center text-[#71717A] mb-8 text-base ">
            Thank you for your payment. Your transaction was processed
            successfully.
          </p>
        </div>

        {/* Payment Details Box */}
        <div className="bg-[#12121208] rounded-xl p-6 mb-6 space-y-4">
          {/* Amount Paid */}
          <div className="flex justify-between items-center">
            <span className="text-[#71717A] text-sm font-medium">
              Amount Paid
            </span>
            <span className="text-2xl font-bold text-[#18181B]">
              $ {formatPrice(bookingdetails?.final_price_without_security)}
            </span>
          </div>

          {/* Booking ID */}
          <div className="flex justify-between items-center">
            <span className="text-[#71717A] font-medium">Booking ID</span>
            <div className="flex items-center gap-2">
              <span className="text-[#18181B] font-semibold">
                {bookingdetails?.id}
              </span>
              <button
                onClick={handleCopyBookingId}
                className="text-gray-400 hover:text-[#71717A] transition-colors"
                title="Copy Booking ID"
              >
                <DocumentDuplicateIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex justify-between items-center">
            <span className="text-[#71717A] font-medium">Pick-up</span>
            <span className="text-[#18181B] font-semibold text-right">
              {bookingdetails?.pick_up_time ? (
                <>
                  {moment(bookingdetails.pick_up_time).format(
                    "ddd, D MMM YYYY",
                  )}{" "}
                  {ourCompany?.default_time_format === "24_hour"
                    ? moment(bookingdetails.pick_up_time).format("HH:mm")
                    : moment(bookingdetails.pick_up_time).format("hh:mm A")}
                </>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          {/* Drop-off Time */}
          <div className="flex justify-between items-center">
            <span className="text-[#71717A] font-medium">Drop-off</span>
            <span className="text-[#18181B] font-semibold text-right">
              {bookingdetails?.drop_off_time ? (
                <>
                  {moment(bookingdetails.drop_off_time).format(
                    "ddd, D MMM YYYY",
                  )}{" "}
                  {ourCompany?.default_time_format === "24_hour"
                    ? moment(bookingdetails.drop_off_time).format("HH:mm")
                    : moment(bookingdetails.drop_off_time).format("hh:mm A")}
                </>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          {/* Payment Method */}
          {/* <div className="flex justify-between items-center">
            <span className="text-[#71717A] font-medium">Payment Method</span>
            <div className="flex items-center gap-2">
              <div className="w-8 h-6 bg-blue-600 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">visa</span>
              </div>
              <span className="text-[#18181B] font-semibold">
                {paymentMethod} ending in {displayLastFour}
              </span>
            </div>
          </div> */}
        </div>

        {/* Complete Pending Steps Button */}
        <ButtonPrimary
          onClick={handleComplete}
          className="w-full text-on-primary  font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
        >
          Complete Pending Steps
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </ButtonPrimary>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-text-color">
              Redirecting to booking details...
            </span>
            <span className="text-sm text-text-color font-medium">
              {countdown}s
            </span>
          </div>
          <div className="w-full bg-secondary-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-6000 h-2 rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Footer Text */}
        <p className="text-center text-text-color text-sm mt-6">
          A confirmation email has been sent to{" "}
          <span className="text-text-color font-medium">
            {bookingdetails?.customer?.email}
          </span>
          .
          <br />
          {/* Need help?{" "}
          <a
            href="/contact"
            className="text-green-500 hover:text-green-600 font-semibold"
          >
            Contact Support
          </a> */}
        </p>
      </div>
    </div>
  );
}
